// Client-side background removal using @imgly/background-removal.
// The SDK downloads its own ONNX model + WASM runtime from
// staticimgly.com/@imgly/background-removal-DATA (data package, not code).
// We pin that CDN explicitly so the library doesn't try to resolve resources
// against our own origin (which would hit CRA's SPA fallback and return
// HTML in place of .wasm/.onnx files — the "expected magic word" error).

let _remove = null;
const loadLib = async () => {
  if (_remove) return _remove;
  const mod = await import("@imgly/background-removal");
  _remove = mod.removeBackground || mod.default;
  return _remove;
};

export const removeBackground = async (input, onProgress) => {
  const remove = await loadLib();
  const config = {
    output: { format: "image/png", quality: 0.9 },
    // IMPORTANT: trailing slash required. Data CDN (not code CDN).
    publicPath: "https://staticimgly.com/@imgly/background-removal-data/1.7.0/dist/",
    debug: false,
  };
  if (typeof onProgress === "function") {
    config.progress = (_key, current, total) => {
      const pct = total ? current / total : 0;
      onProgress(Math.min(1, pct));
    };
  }
  return remove(input, config);
};

// Parse "#rrggbb" → [r,g,b]
const parseHex = (c) => {
  const h = (c || "#ffffff").replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

// Fringe decontamination: @imgly keeps the ORIGINAL (often dark) RGB on
// semi-transparent edge pixels. Standard alpha compositing then produces a
// visible dark halo (fg * alpha + bg * (1-alpha) with dark fg ⇒ dark edge).
// We "unmatte" the edge by pulling the fg RGB toward the target bg so the
// final composite is clean.
//   fg_clean = fg - bg * (1 - alpha_norm) ... scaled by 1/alpha_norm, clamped.
// We only touch alpha ∈ (0, 254) — fully opaque / transparent pixels pass
// through unchanged, so the subject's own pixels never shift.
const decontaminateFringe = (imgData, bgHex) => {
  const [bR, bG, bB] = parseHex(bgHex);
  const d = imgData.data;
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3];
    if (a === 0 || a === 255) continue;
    const an = a / 255;
    const inv = 1 - an;
    // Un-matte: recover approximate pure foreground color.
    const r = (d[i]     - bR * inv) / an;
    const g = (d[i + 1] - bG * inv) / an;
    const b = (d[i + 2] - bB * inv) / an;
    d[i]     = r < 0 ? 0 : r > 255 ? 255 : r;
    d[i + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
    d[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
  }
};

// Composite the transparent PNG blob onto a solid background color and
// return a data URL. Used to produce the final "beyaz arka planlı" photo.
export const compositeOnColor = (blob, color = "#ffffff") =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      // 1) Draw the transparent PNG to its own buffer so we can clean edges.
      const buf = document.createElement("canvas");
      buf.width = img.width; buf.height = img.height;
      const bctx = buf.getContext("2d", { willReadFrequently: true });
      bctx.drawImage(img, 0, 0);
      try {
        const data = bctx.getImageData(0, 0, img.width, img.height);
        decontaminateFringe(data, color);
        bctx.putImageData(data, 0, 0);
      } catch (_) { /* taint — skip, worst case = original fringe */ }
      // 2) Paint target bg + composite cleaned FG on top.
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(buf, 0, 0);
      URL.revokeObjectURL(url);
      resolve({ dataUrl: canvas.toDataURL("image/jpeg", 0.95), w: img.width, h: img.height });
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
